<script lang="ts" setup>
import { computed, ref, type PropType, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import type { MarketModel } from '@exchange/libs/market/service/src';
import { TimeInForceType } from '@exchange/libs/order/shared-model/src/lib/order-essentials';
import { settingsService } from '@exchange/libs/settings/service/src';
import type { OrderAdvancedSettings } from '@exchange/libs/settings/service/src';

const { t } = useI18n({ useScope: 'global' });

const props = defineProps({
  market: { type: Object as PropType<MarketModel>, required: true },
});

const orderAdvanced = computed(() => settingsService.settings.userSettings.orderAdvanced);
const advancedOn = ref(true);

const defaultTimeInForce = computed(() => ({
  value: TimeInForceType.GOOD_TILL_CANCELLED,
  label: t('modules.orderForm.advancedOptions.timeInForceNames.GOOD_TILL_CANCELLED'),
}));

const postOnlyTimeInForce = computed(() => ({
  value: TimeInForceType.POST_ONLY,
  label: t('modules.orderForm.advancedOptions.timeInForceNames.POST_ONLY'),
}));

const timeInForceOptions = computed(() => {
  if (props.market.state === 'POST_ONLY') {
    return [postOnlyTimeInForce.value];
  }

  return [
    defaultTimeInForce.value,
    {
      value: TimeInForceType.IMMEDIATE_OR_CANCELLED,
      label: t('modules.orderForm.advancedOptions.timeInForceNames.IMMEDIATE_OR_CANCELLED'),
    },
    {
      value: TimeInForceType.FILL_OR_KILL,
      label: t('modules.orderForm.advancedOptions.timeInForceNames.FILL_OR_KILL'),
    },
    // POST_ONLY is now a time in force option in the exchange, because you can only have GTC post only
    postOnlyTimeInForce.value,
  ];
});
const selectedTimeInForce = computed(() => {
  const tif = timeInForceOptions.value.find((e) => e.value === orderAdvanced.value.timeInForce);

  return tif || defaultTimeInForce.value;
});

const defaultCancelAfter = computed(() => ({
  value: 60,
  label: t('modules.orderForm.advancedOptions.cancelAfterOptions.hour'),
}));
const cancelAfterOptions = computed(() => [
  {
    value: 1,
    label: t('modules.orderForm.advancedOptions.cancelAfterOptions.mins', { number: 1 }),
  },
  {
    value: 3,
    label: t('modules.orderForm.advancedOptions.cancelAfterOptions.mins', { number: 3 }),
  },
  {
    value: 5,
    label: t('modules.orderForm.advancedOptions.cancelAfterOptions.mins', { number: 5 }),
  },
  {
    value: 15,
    label: t('modules.orderForm.advancedOptions.cancelAfterOptions.mins', { number: 15 }),
  },
  {
    value: 30,
    label: t('modules.orderForm.advancedOptions.cancelAfterOptions.mins', { number: 30 }),
  },
  defaultCancelAfter.value,
  {
    value: 240,
    label: t('modules.orderForm.advancedOptions.cancelAfterOptions.hours', { number: 4 }),
  },
  {
    value: 360,
    label: t('modules.orderForm.advancedOptions.cancelAfterOptions.hours', { number: 6 }),
  },
  {
    value: 720,
    label: t('modules.orderForm.advancedOptions.cancelAfterOptions.hours', { number: 12 }),
  },
  {
    value: 1440,
    label: t('modules.orderForm.advancedOptions.cancelAfterOptions.day'),
  },
  {
    value: 10080,
    label: t('modules.orderForm.advancedOptions.cancelAfterOptions.week'),
  },
]);
const selectedCancelAfter = computed(() => {
  const ca = cancelAfterOptions.value.find((e) => e.value === orderAdvanced.value.cancelAfter);

  return ca || defaultCancelAfter.value;
});

const advancedHint = computed(() => {
  let info = '';

  switch (selectedTimeInForce.value.value) {
    case TimeInForceType.GOOD_TILL_CANCELLED:
    case TimeInForceType.IMMEDIATE_OR_CANCELLED:
      info = t('modules.orderForm.advancedOptions.executionHints.IMMEDIATE_OR_CANCELLED');
      break;
    case TimeInForceType.FILL_OR_KILL:
      info = t('modules.orderForm.advancedOptions.executionHints.FILL_OR_KILL');
      break;
    case TimeInForceType.POST_ONLY:
      info = t('modules.orderForm.advancedOptions.executionHints.POST_ONLY');
      break;
    default:
      break;
  }

  return {
    info,
    url: t('modules.orderForm.advancedOptions.hint.url'),
    urlText: t('modules.orderForm.advancedOptions.hint.text'),
  };
});

const setSettings = (path: string, value: unknown) => settingsService.setSettings({ path, value });
const updateSelectedTimeInForce = async (value: { value: TimeInForceType; label: string }) => {
  const timeInForce = value.value;
  const updatedSettings: Partial<OrderAdvancedSettings> = {
    ...orderAdvanced.value,
    cancelAfter: selectedCancelAfter.value.value,
    enabled: advancedOn.value,
    timeInForce,
  };

  await setSettings('orderAdvanced', updatedSettings);
};

const updateSelectedCancelAfter = async (value: { value: number; label: string }) => {
  const cancelAfter = value.value;
  const updatedSettings: Partial<OrderAdvancedSettings> = {
    ...orderAdvanced.value,
    cancelAfter,
    enabled: advancedOn.value,
  };

  await setSettings('orderAdvanced', updatedSettings);
};

watch(
  () => props.market,
  () => {
    if (props.market.state === 'POST_ONLY' && selectedTimeInForce.value.value !== TimeInForceType.POST_ONLY) {
      updateSelectedTimeInForce(postOnlyTimeInForce.value);
    }
  },
  {
    immediate: true,
  },
);
</script>

<template>
  <div
    class="order-form-settings"
    :class="{ 'order-form-settings--active': advancedOn }"
    testid="advanced-order"
  >
    <!--
    <div class="order-form-settings__item">
      <ot-checkbox-positive-ui
        v-model:selected="advancedOn"
        class="order-form-settings__checkbox"
        :vertically-centered="true"
        label-position="left"
        :label="$t('modules.orderForm.advancedOptions.advanced')"
        :on-check-action="updateAdvancedOn"
        :on-check-action-failed="onSettingsUpdateFailed"
      />
    </div>
    -->

    <ot-transition-collapse origin="top center">
      <div
        v-show="advancedOn"
        class="order-form-settings__body"
      >
        <div class="order-form-settings__body-item order-form-settings__body-item--first">
          <label
            class="order-form-settings__label"
            for="time-in-force"
          >
            {{ $t('modules.orderForm.advancedOptions.timeInForce') }}
          </label>
          <ot-select
            class="order-form-settings__select"
            variant="dark-outlined"
            input-id="time-in-force"
            :menu-top-space="true"
            label="label"
            :options="timeInForceOptions"
            :value="selectedTimeInForce"
            testid="order-time-in-force"
            @update:value="updateSelectedTimeInForce"
          />
        </div>
        <div
          v-if="showCancelAfter"
          class="order-form-settings__body-item"
        >
          <label
            class="order-form-settings__label"
            for="cancel-after"
          >
            {{ $t('modules.orderForm.advancedOptions.cancelAfter') }}
          </label>
          <ot-select
            class="order-form-settings__select cancel-after-select"
            testid="order-cancel-after"
            variant="dark-outlined"
            input-id="cancel-after"
            label="label"
            dropdown-max-height="150px"
            :menu-top-space="true"
            :options="cancelAfterOptions"
            :value="selectedCancelAfter"
            @update:value="updateSelectedCancelAfter"
          />
        </div>
        <div class="order-form-settings__info">
          <p class="order-form-settings__info-text">
            {{ advancedHint.info }}
            <x-link-external
              class="order-form-settings__info-link"
              :href="advancedHint.url"
              :use-system-browser="true"
            >
              {{ advancedHint.urlText }}
            </x-link-external>
          </p>
        </div>
      </div>
    </ot-transition-collapse>
  </div>
</template>

<style lang="scss">
.order-form-settings {
  --of-padding-x: 10px;

  .xs &,
  .sm &,
  .md & {
    --of-padding-x: var(--padding-x-xs);
  }

  padding: 0;
  border-top: 2px solid rgb(var(--v-theme-elevation-1));
  border-bottom: 2px solid rgb(var(--v-theme-elevation-1));
  margin: 8px 0 0;
  color: rgb(var(--v-theme-text-secondary));
  letter-spacing: normal;
  transition: 0.4s;

  &__item,
  &__body-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: var(--font-size-md);
    font-weight: var(--font-weight-medium);
  }

  &__item {
    padding: 10px 0;

    .xs & {
      padding: 10px 8px;
    }
  }

  &__body-item {
    padding: 8px 0;

    .xs & {
      flex-direction: column;
      align-items: start;
    }

    + .order-form-settings__body-item {
      border-top: 1px solid rgb(var(--v-theme-elevation-1));
    }

    &--first {
      padding-top: 0;
    }
  }

  &__label {
    min-width: max-content;

    .xs & {
      font-size: var(--font-size-sm);
    }
  }

  &__checkbox {
    width: 100%;
  }

  &__select {
    width: 60%;
    color: rgb(var(--v-theme-text-primary));

    .xs & {
      width: 100%;
    }

    --ot-select-dropdown-menu-width: max-content;
    --ot-select-dropdown-menu-text-align: right;
  }

  &__info {
    padding-top: 4px;
    color: rgb(var(--v-theme-text-tertiary));
    font-weight: var(--font-weight-medium);
    line-height: var(--app-line-height-smaller);

    &-link {
      color: inherit;
    }
  }
}

.order-form-settings--active {
  background-color: transparent;
}
</style>
